import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const InvestorArchitecture = () => {
  const steps = [
    {
      title: "Vault Access Request",
      desc: "Investors request access to QNC’s live vaults. Each vault is pre-deployed and operated under audited, non-custodial smart contracts.",
      details: [
        "Select from QNC’s live, strategy-driven vaults managed under Enzyme’s verified smart contracts.",
        "Each vault is audited, non-custodial, and deploys within 48 hours.",
      ],
    },
    {
      title: "Capital Allocation",
      desc: "Once approved, you allocate capital directly into the vault via smart contract. No intermediaries, no counterparty risk.",
      details: [
        "Allocate directly from your wallet to the chosen vault.",
        "Smart contracts issue proportional vault tokens representing your ownership while strategies execute automatically with full on-chain visibility.",
       
      ],
    },
    {
      title: "On-Chain Tracking & Withdrawals",
      desc: "Performance, reporting, and fee logic are fully automated — providing complete visibility and control.",
      details: [
        "All trades, returns, and fees are automated and verifiable in real time via Enzyme dashboard.",
        "Withdraw anytime (unless voluntary lockup terms are chosen).",
        "Profit distributions and fees are enforced above the high-watermark.",
      ],
    },
  ];

  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="architecture"
      className="relative py-20 md:py-28 bg-gradient-to-b from-[#050B1A] via-[#081B3F] to-[#0A2A66] text-white overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Section Label */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-xs tracking-wider font-semibold uppercase text-[#A5F5E2] bg-white/10 px-4 py-1 rounded-full">
            Architecture
          </span>
        </motion.div>

        {/* Heading */}
        <motion.h2
          className="text-3xl md:text-5xl font-bold text-center mb-16 md:mb-20 text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          How It Works
        </motion.h2>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical connector line (desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 -ml-0.5 w-1 h-full bg-gradient-to-b from-[#4DB2D3] via-[#3D8FAA] to-[#A5F5E2] rounded-full"></div>

          <div className="space-y-12 md:space-y-20">
            {steps.map((s, idx) => {
              const isOpen = openIndex === idx;
              return (
                <motion.div
                  key={idx}
                  className={`relative flex flex-col md:flex-row items-center ${
                    idx % 2 === 0 ? "md:flex-row-reverse" : ""
                  }`}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? 60 : -60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.2 }}
                >
                  {/* Marker (desktop only) */}
                  <div className="hidden md:flex absolute left-1/2 -ml-6 items-center justify-center w-12 h-12 rounded-full bg-gradient-to-r from-[#4DB2D3] to-[#3D8FAA] text-white font-semibold shadow-lg ring-4 ring-[#0A2A66] z-10">
                    {idx + 1}
                  </div>

                  {/* Card */}
                  <div
                    className={`group relative bg-white/5 border border-white/10 rounded-2xl shadow-lg hover:border-[#4DB2D3]/40 backdrop-blur-md transition-all w-full md:w-5/12 p-6 md:p-8 overflow-hidden ${
                      idx % 2 === 0
                        ? "md:mr-auto md:pr-12"
                        : "md:ml-auto md:pl-12"
                    }`}
                  >
                    {/* Mobile marker */}
                    <div className="flex md:hidden items-center mb-3">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-[#4DB2D3] to-[#3D8FAA] text-white font-semibold shadow-md mr-3">
                        {idx + 1}
                      </div>
                      <h3 className="text-lg font-semibold text-[#A5F5E2]">
                        {s.title}
                      </h3>
                    </div>

                    {/* Desktop title */}
                    <h3 className="hidden md:block text-xl font-semibold text-[#A5F5E2] mb-3">
                      {s.title}
                    </h3>

                    <p className="text-white/80 leading-relaxed">{s.desc}</p>

                    {/* Toggle button */}
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : idx)}
                      className="mt-4 text-sm font-medium text-[#4DB2D3] hover:text-[#A5F5E2] transition-colors"
                    >
                      {isOpen ? "Hide details ▲" : "View details ▼"}
                    </button>

                    {/* Expandable details */}
                    <AnimatePresence>
                      {isOpen && (
                        <motion.ul
                          className="mt-4 pl-4 space-y-2 text-white/70 text-sm list-disc"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.4 }}
                        >
                          {s.details.map((d, i) => (
                            <li key={i}>{d}</li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Footer Note */}
        <motion.p
          className="mt-16 md:mt-20 text-center text-white/60 max-w-2xl mx-auto text-sm md:text-base"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          Vault access and allocation are live within 48 hours. Performance,
          reporting, and fee logic remain fully transparent and automated
          through Enzyme’s on-chain architecture.
        </motion.p>
      </div>
    </section>
  );
};

export default InvestorArchitecture;
